import { MarkerClusterer, GridAlgorithm } from "@googlemaps/markerclusterer";

import '../sass/school-map.scss';

const MAP_STYLING = [
    {
        "featureType": "landscape",
        "stylers": [
            {
                "saturation": -100
            },
            {
                "lightness": 40
            }
        ]
    },
    {
        "featureType": "poi",
        "stylers": [
            {
                "saturation": -100
            }
        ]
    },
    {
        "featureType": "road",
        "stylers": [
            {
                "saturation": -100
            }
        ]
    },
    {
        "featureType": "road",
        "elementType": "labels",
        "stylers": [
            {
                "visibility": "off"
            }
        ]
    },
    {
        "featureType": "water",
        "stylers": [
            {
                "saturation": -100
            },
            {
                "lightness": 40
            }
        ]
    }
];

const MARKER_COLOUR = '#19284C';
const MARKER_LABEL_COLOUR = '#F7F3F1';
const POSTCODE_RADIUS_MILES = 50;
const MILES_TO_METERS = 1609.344;

(() => {
    const googleMapsScript = document.querySelector('#ofg-schools-google-maps');
    const $blocks = document.querySelectorAll('.block.school-map');

    if (!$blocks.length) return;

    $blocks.forEach($block => {
        const $main = $block.querySelector('.school-map__main');
        const $map = $block.querySelector('.school-map__map');
        const $list = $block.querySelector('.school-map__list');
        const $viewButtons = [...$block.querySelectorAll('.school-map__view-btn')];
        const $filter = $block.querySelector('.school-map__filter');
        const $filterToggle = $filter?.querySelector('.school-map__filter-toggle');
        const $filterBody = $filter?.querySelector('.school-map__filter-body');
        const $filterName = $filter?.querySelector('[name="name"]');
        const $filterPostcode = $filter?.querySelector('[name="postcode"]');
        const $filterLocation = $filter?.querySelector('[name="location"]');
        const $filterSpecialism = $filter?.querySelector('[name="specialism"]');
        const $suggestions = $filter?.querySelector('.school-map__suggestions');
        const $typeButtons = $filter ? [...$filter.querySelectorAll('.school-map__type-btn')] : [];
        const $countLabel = $block.querySelector('.school-map__count');
        const $resetBtn = $block.querySelector('.school-map__reset-btn');
        const $listItems = $list ? [...$list.querySelectorAll('.school-map-card--list')] : [];
        const suggestionIdPrefix = $suggestions?.id || 'school-map-suggestion';

        let map;
        let schools = [];
        let mapMarkers = [];
        let markerCluster = null;
        let searchMarker = null;
        let searchTimeout = null;
        let selectedSchoolId = null;
        let suggestionIndex = -1;
        let postcodeOrigin = null;
        let geocodeRequestId = 0;
        const geocodeCache = {};

        const getSelectedTypes = () => {
            return $typeButtons
                .filter($button => $button.getAttribute('aria-checked') === 'true')
                .map($button => $button.dataset.type)
                .filter(Boolean);
        };

        const normalizePostcode = value => String(value || '').replace(/\s+/g, '').toLowerCase();

        const toRadians = degrees => degrees * Math.PI / 180;

        const distanceMiles = (from, to) => {
            if (!from || !to || from.lat == null || from.lng == null || to.lat == null || to.lng == null) {
                return Infinity;
            }

            const fromLat = Number(from.lat);
            const fromLng = Number(from.lng);
            const toLat = Number(to.lat);
            const toLng = Number(to.lng);
            const dLat = toRadians(toLat - fromLat);
            const dLng = toRadians(toLng - fromLng);
            const lat1 = toRadians(fromLat);
            const lat2 = toRadians(toLat);
            const haversine = Math.sin(dLat / 2) ** 2
                + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;

            return 2 * 3958.8 * Math.asin(Math.min(1, Math.sqrt(haversine)));
        };

        const geocodePostcode = query => {
            const normalized = normalizePostcode(query);

            if (!normalized || normalized.length < 2) {
                return Promise.resolve(null);
            }

            if (Object.prototype.hasOwnProperty.call(geocodeCache, normalized)) {
                return Promise.resolve(geocodeCache[normalized]);
            }

            if (typeof google === 'undefined' || !google.maps?.Geocoder) {
                return Promise.resolve(null);
            }

            const geocoder = new google.maps.Geocoder();

            return new Promise(resolve => {
                geocoder.geocode({
                    address: query.trim(),
                    componentRestrictions: { country: 'GB' },
                    region: 'GB',
                }, (results, status) => {
                    if (status === 'OK' && results?.[0]?.geometry?.location) {
                        const location = results[0].geometry.location;
                        const origin = {
                            lat: location.lat(),
                            lng: location.lng(),
                        };

                        geocodeCache[normalized] = origin;
                        resolve(origin);
                        return;
                    }

                    geocodeCache[normalized] = null;
                    resolve(null);
                });
            });
        };

        const getFilterState = () => ({
            types: getSelectedTypes(),
            name: $filterName?.value ? $filterName.value.trim().toLowerCase() : '',
            postcode: $filterPostcode?.value ? $filterPostcode.value.trim() : '',
            postcodeOrigin,
            location: $filterLocation?.value || '',
            specialism: $filterSpecialism?.value || '',
            schoolId: selectedSchoolId,
        });

        const schoolMatchesFilter = (school, filter, requireCoords = false) => {
            if (requireCoords && (!school?.location?.lat || !school?.location?.lng)) return false;

            if (filter.types.length && !filter.types.includes(school.schoolType)) {
                return false;
            }

            if (filter.schoolId) {
                if (String(school.id) !== String(filter.schoolId)) {
                    return false;
                }
            } else if (filter.name && !(school.title || '').toLowerCase().includes(filter.name)) {
                return false;
            }

            if (filter.postcode) {
                if (!filter.postcodeOrigin || !school?.location?.lat || !school?.location?.lng) {
                    return false;
                }

                if (distanceMiles(filter.postcodeOrigin, school.location) > POSTCODE_RADIUS_MILES) {
                    return false;
                }
            }

            if (filter.location && !school.locations?.includes(filter.location)) {
                return false;
            }

            if (filter.specialism && !school.specialisms?.includes(filter.specialism)) {
                return false;
            }

            return true;
        };

        const escapeHtml = value => String(value)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');

        const highlightMatch = (title, query) => {
            const index = title.toLowerCase().indexOf(query.toLowerCase());

            if (index === -1) {
                return escapeHtml(title);
            }

            const before = escapeHtml(title.slice(0, index));
            const match = escapeHtml(title.slice(index, index + query.length));
            const after = escapeHtml(title.slice(index + query.length));

            return `${before}<span class="school-map__suggestion-match">${match}</span>${after}`;
        };

        const getNameSuggestions = query => {
            if (!query) return [];

            const queryLower = query.toLowerCase();
            const filterWithoutName = {
                ...getFilterState(),
                name: '',
                schoolId: null,
            };

            return schools
                .filter(school => {
                    if (!schoolMatchesFilter(school, filterWithoutName)) return false;
                    return (school.title || '').toLowerCase().includes(queryLower);
                })
                .sort((a, b) => {
                    const aTitle = (a.title || '').toLowerCase();
                    const bTitle = (b.title || '').toLowerCase();
                    const aStarts = aTitle.startsWith(queryLower);
                    const bStarts = bTitle.startsWith(queryLower);

                    if (aStarts && !bStarts) return -1;
                    if (!aStarts && bStarts) return 1;

                    return aTitle.localeCompare(bTitle);
                })
                .slice(0, 8);
        };

        const setSuggestionExpanded = isOpen => {
            if ($filterName) {
                $filterName.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
            }

            if (!isOpen && $filterName) {
                $filterName.removeAttribute('aria-activedescendant');
            }
        };

        const hideSuggestions = () => {
            suggestionIndex = -1;

            if (!$suggestions) return;

            $suggestions.hidden = true;
            $suggestions.innerHTML = '';
            setSuggestionExpanded(false);
        };

        const highlightSuggestion = index => {
            if (!$suggestions) return;

            const $options = [...$suggestions.querySelectorAll('[role="option"]')];

            suggestionIndex = index;

            $options.forEach(($option, optionIndex) => {
                const isSelected = optionIndex === index;
                $option.setAttribute('aria-selected', isSelected ? 'true' : 'false');

                if (isSelected) {
                    $option.scrollIntoView({ block: 'nearest' });

                    if ($filterName) {
                        $filterName.setAttribute('aria-activedescendant', $option.id);
                    }
                }
            });

            if (index < 0 && $filterName) {
                $filterName.removeAttribute('aria-activedescendant');
            }
        };

        const renderSuggestions = (matches, query) => {
            if (!$suggestions) return;

            if (!matches.length) {
                hideSuggestions();
                return;
            }

            $suggestions.innerHTML = matches.map(school => `
                <li id="${suggestionIdPrefix}-${school.id}"
                    class="school-map__suggestion"
                    role="option"
                    data-id="${escapeHtml(school.id)}"
                    aria-selected="false">
                    ${highlightMatch(school.title || '', query)}
                </li>
            `).join('');

            $suggestions.hidden = false;
            setSuggestionExpanded(true);
            highlightSuggestion(-1);
        };

        const updateSuggestions = () => {
            const query = $filterName?.value ? $filterName.value.trim() : '';

            if (!query) {
                hideSuggestions();
                return;
            }

            renderSuggestions(getNameSuggestions(query), query);
        };

        const selectSuggestion = school => {
            if (!school) return;

            selectedSchoolId = school.id;

            if ($filterName) {
                $filterName.value = school.title || '';
            }

            hideSuggestions();
            applyFilters();
        };

        const selectSuggestionByIndex = index => {
            const matches = getNameSuggestions($filterName?.value ? $filterName.value.trim() : '');
            selectSuggestion(matches[index]);
        };

        const updateList = (filter = getFilterState()) => {
            $listItems.forEach($item => {
                const school = schools.find(item => String(item.id) === String($item.dataset.id));

                $item.hidden = !school || !schoolMatchesFilter(school, filter);
            });
        };

        const applyFilters = () => {
            const postcodeQuery = $filterPostcode?.value ? $filterPostcode.value.trim() : '';
            const requestId = ++geocodeRequestId;

            const run = filter => {
                if (requestId !== geocodeRequestId) return;

                const matchingSchools = schools.filter(school => schoolMatchesFilter(school, filter));

                updateCountLabel(matchingSchools.length);
                updateList(filter);

                if ($map) {
                    $map.dispatchEvent(new CustomEvent('filter-update', {
                        detail: filter
                    }));
                }
            };

            if (!postcodeQuery) {
                postcodeOrigin = null;
                run(getFilterState());
                return;
            }

            geocodePostcode(postcodeQuery).then(origin => {
                if (requestId !== geocodeRequestId) return;

                postcodeOrigin = origin;
                run(getFilterState());
            });
        };

        const setView = view => {
            if (!$main) return;

            $main.classList.toggle('school-map__main--list', view === 'list');

            if ($list) {
                $list.hidden = view !== 'list';
            }

            $viewButtons.forEach($button => {
                $button.setAttribute('aria-selected', $button.dataset.view === view ? 'true' : 'false');
            });

            if (view === 'map' && map && window.google?.maps?.event) {
                google.maps.event.trigger(map, 'resize');
                resizeMap();
            }
        };

        const filterLocations = () => {
            applyFilters();
        };

        const resizeMap = async () => {
            if (!map) return;

            const { LatLngBounds, LatLng, Circle } = await google.maps;
            const filter = getFilterState();

            if (filter.postcodeOrigin) {
                const circle = new Circle({
                    center: new LatLng(filter.postcodeOrigin.lat, filter.postcodeOrigin.lng),
                    radius: POSTCODE_RADIUS_MILES * MILES_TO_METERS,
                });

                map.fitBounds(circle.getBounds());
                return;
            }

            if (!mapMarkers.length) return;

            const bounds = new LatLngBounds();

            mapMarkers.forEach(mapMarker => {
                const lat = mapMarker?.position?.lat();
                const lng = mapMarker?.position?.lng();
                bounds.extend(new LatLng(lat, lng));
            });

            if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
                bounds.extend(new google.maps.LatLng(bounds.getNorthEast().lat() + 0.02, bounds.getNorthEast().lng() + 0.02));
                bounds.extend(new google.maps.LatLng(bounds.getNorthEast().lat() - 0.02, bounds.getNorthEast().lng() - 0.02));
            }

            map.fitBounds(bounds);
        };

        const updateCountLabel = count => {
            if (!$countLabel) return;

            let labelText = $countLabel.dataset.labelMultiple;

            if (count === 1) {
                labelText = $countLabel.dataset.labelSingle;
            }

            $countLabel.innerHTML = labelText.replace('{count}', count);
        };

        const getMarkerIcon = (Size, fill) => {
            const svg = window.btoa(`
                <svg fill="${fill}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 36 36">
                    <circle cx="18" cy="18" r="18" />
                </svg>`);

            return {
                url: `data:image/svg+xml;base64,${svg}`,
                scaledSize: new Size(36, 36),
            };
        };

        const getSearchMarkerIcon = (Size, Point) => {
            const svg = window.btoa(`
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16">
                    <circle cx="8" cy="8" r="6" fill="#FFFFFF" stroke="#FFF" stroke-width="2" />
                </svg>`);

            return {
                url: `data:image/svg+xml;base64,${svg}`,
                scaledSize: new Size(16, 16),
                anchor: new Point(8, 8),
            };
        };

        const initMarkers = async (filter = getFilterState()) => {
            const { InfoWindow, LatLng, Marker, Point, Size } = await google.maps;

            const infoWindow = new InfoWindow({
                content: '',
                maxWidth: 320,
                disableAutoPan: false,
            });

            infoWindow.addListener('domready', () => {
                const $close = $map?.querySelector('.school-map-card__close');

                $close?.addEventListener('click', e => {
                    e.preventDefault();
                    e.stopPropagation();
                    infoWindow.close();
                });
            });

            mapMarkers.forEach(marker => {
                marker.setMap(null);
            });

            if (searchMarker) {
                searchMarker.setMap(null);
                searchMarker = null;
            }

            if (markerCluster) {
                markerCluster.setMap(null);
            }

            const filteredSchools = schools.filter(school => schoolMatchesFilter(school, filter, true));

            mapMarkers = filteredSchools.map(school => {
                const position = new LatLng(school.location.lat, school.location.lng);

                const marker = new Marker({
                    position,
                    icon: getMarkerIcon(Size, MARKER_COLOUR),
                    label: {
                        text: '1',
                        color: MARKER_LABEL_COLOUR,
                        fontSize: '16px',
                        fontFamily: "'Outfit', sans-serif",
                    }
                });

                marker.addListener('click', () => {
                    if (infoWindow.isOpen) {
                        infoWindow.close();

                        if (infoWindow.position.lat() === marker.position.lat() && infoWindow.position.lng() === marker.position.lng()) {
                            return;
                        }
                    }

                    infoWindow.setContent(school.cardHtml);

                    infoWindow.open({
                        anchor: marker,
                        map
                    });
                });

                return marker;
            });

            updateCountLabel(schools.filter(school => schoolMatchesFilter(school, filter)).length);

            if (mapMarkers.length) {
                markerCluster = new MarkerClusterer({
                    markers: mapMarkers,
                    map,
                    onClusterClick: (e, cluster) => {
                        infoWindow.close();
                        map.fitBounds(cluster.bounds);
                        return true;
                    },
                    algorithm: new GridAlgorithm({
                        maxDistance: 40000,
                        gridSize: 36
                    }),
                    renderer: {
                        render({ count, position }) {
                            return new Marker({
                                position,
                                icon: getMarkerIcon(Size, MARKER_COLOUR),
                                label: {
                                    text: String(count),
                                    color: MARKER_LABEL_COLOUR,
                                    fontSize: '16px',
                                    fontFamily: "'Outfit', sans-serif",
                                },
                                zIndex: Number(Marker.MAX_ZINDEX) + count,
                            });
                        }
                    }
                });
            }

            if (filter.postcodeOrigin) {
                searchMarker = new Marker({
                    position: new LatLng(filter.postcodeOrigin.lat, filter.postcodeOrigin.lng),
                    map,
                    icon: getSearchMarkerIcon(Size, Point),
                    clickable: false,
                    zIndex: Number(Marker.MAX_ZINDEX) + 1000,
                });
            }

            resizeMap();
        };

        const filterMapLocations = e => {
            initMarkers(e.detail);
        };

        const initMap = async () => {
            if (!$map || typeof google === 'undefined' || !google.maps) return;

            const { Map } = await google.maps;

            schools = $main?.dataset?.schools ? JSON.parse($main.dataset.schools) : [];

            map = new Map($map, {
                center: { lat: 54.5, lng: -2.5 },
                zoom: 6,
                clickableIcons: false,
                styles: MAP_STYLING,
                disableDefaultUI: true,
                cameraControl: true,
            });

            new google.maps.Polygon({
                paths: [
                    [
                        { lat: 85, lng: -179.9999 },
                        { lat: -85, lng: -179.9999 },
                        { lat: -85, lng: 0 },
                        { lat: 85, lng: 0 }
                    ],
                    [
                        { lat: 85, lng: 0 },
                        { lat: -85, lng: 0 },
                        { lat: -85, lng: 179.9999 },
                        { lat: 85, lng: 179.9999 }
                    ]
                ],
                strokeWeight: 0,
                fillColor: "#19284C",
                fillOpacity: 0.45,
                map,
                clickable: false,
                zIndex: 1
            });

            $map.addEventListener('filter-update', filterMapLocations);

            applyFilters();
        };

        const mobileFilterMq = window.matchMedia('(max-width: 1023px)');

        const setFilterOpen = isOpen => {
            if (!$filterToggle || !$filterBody) return;

            $filterToggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
            $filterBody.hidden = !isOpen && mobileFilterMq.matches;
        };

        const initFilter = () => {
            setFilterOpen(!mobileFilterMq.matches);

            $filterToggle?.addEventListener('click', () => {
                if (!mobileFilterMq.matches) return;

                const isOpen = $filterToggle.getAttribute('aria-expanded') === 'true';
                setFilterOpen(!isOpen);
            });

            if (mobileFilterMq.addEventListener) {
                mobileFilterMq.addEventListener('change', () => {
                    setFilterOpen(!mobileFilterMq.matches);
                });
            }

            if ($filter) {
                $filter.addEventListener('submit', e => {
                    e.preventDefault();

                    if (suggestionIndex >= 0 && $suggestions && !$suggestions.hidden) {
                        selectSuggestionByIndex(suggestionIndex);
                        return;
                    }

                    hideSuggestions();
                });
            }

            $typeButtons.forEach($button => {
                $button.addEventListener('click', () => {
                    const isSelected = $button.getAttribute('aria-checked') === 'true';

                    $typeButtons.forEach($typeButton => {
                        $typeButton.setAttribute('aria-checked', 'false');
                    });

                    if (!isSelected) {
                        $button.setAttribute('aria-checked', 'true');
                    }

                    filterLocations();
                });
            });

            if ($filterName) {
                $filterName.addEventListener('input', () => {
                    selectedSchoolId = null;
                    updateSuggestions();
                    clearTimeout(searchTimeout);
                    searchTimeout = setTimeout(filterLocations, 200);
                });

                $filterName.addEventListener('focus', updateSuggestions);

                $filterName.addEventListener('blur', () => {
                    hideSuggestions();
                });

                $filterName.addEventListener('keydown', e => {
                    const isOpen = $suggestions && !$suggestions.hidden;
                    const optionCount = isOpen
                        ? $suggestions.querySelectorAll('[role="option"]').length
                        : 0;

                    if (e.key === 'ArrowDown') {
                        e.preventDefault();

                        if (!isOpen) {
                            updateSuggestions();
                            return;
                        }

                        highlightSuggestion(suggestionIndex < optionCount - 1 ? suggestionIndex + 1 : 0);
                    }

                    if (e.key === 'ArrowUp') {
                        e.preventDefault();

                        if (!isOpen) return;

                        highlightSuggestion(suggestionIndex > 0 ? suggestionIndex - 1 : optionCount - 1);
                    }

                    if (e.key === 'Enter') {
                        if (isOpen && suggestionIndex >= 0) {
                            e.preventDefault();
                            selectSuggestionByIndex(suggestionIndex);
                            return;
                        }

                        hideSuggestions();
                    }

                    if (e.key === 'Escape') {
                        hideSuggestions();
                    }
                });
            }

            if ($filterPostcode) {
                $filterPostcode.addEventListener('input', () => {
                    clearTimeout(searchTimeout);
                    searchTimeout = setTimeout(filterLocations, 400);
                });
            }

            if ($suggestions) {
                $suggestions.addEventListener('mousedown', e => {
                    e.preventDefault();

                    const $option = e.target.closest('[role="option"]');

                    if (!$option) return;

                    const school = schools.find(item => String(item.id) === String($option.dataset.id));
                    selectSuggestion(school);
                });
            }

            document.addEventListener('click', e => {
                if (!$filterName || e.target === $filterName || $suggestions?.contains(e.target)) return;
                hideSuggestions();
            });

            if ($filterLocation) {
                $filterLocation.addEventListener('change', filterLocations);
            }

            if ($filterSpecialism) {
                $filterSpecialism.addEventListener('change', filterLocations);
            }

            $viewButtons.forEach($button => {
                $button.addEventListener('click', () => {
                    setView($button.dataset.view);
                });
            });

            if ($resetBtn) {
                $resetBtn.addEventListener('click', () => {
                    selectedSchoolId = null;
                    postcodeOrigin = null;

                    if ($filterName) $filterName.value = '';
                    if ($filterPostcode) $filterPostcode.value = '';
                    if ($filterLocation) $filterLocation.value = '';
                    if ($filterSpecialism) $filterSpecialism.value = '';

                    $typeButtons.forEach($button => {
                        $button.setAttribute('aria-checked', 'false');
                    });

                    hideSuggestions();
                    filterLocations();
                });
            }
        };

        schools = $main?.dataset?.schools ? JSON.parse($main.dataset.schools) : [];
        initFilter();
        applyFilters();

        const startMap = () => {
            initMap();
        };

        if (typeof google !== 'undefined' && google.maps) {
            startMap();
        } else if (googleMapsScript) {
            googleMapsScript.addEventListener('load', startMap);
        } else {
            window.addEventListener('load', startMap);
        }
    });
})();
